/* AdminPartnershipsTab — long-term organizational partnership tracker.
 *
 * Lives as the FIRST sub-tab inside Community Outreach. Tracks orgs
 * (churches, shelters, schools, nonprofits, healthcare, businesses,
 * government, community leaders), each with: profile, contacts, interaction
 * timeline, opportunities, and pipeline status.
 *
 * Layout: left = pipeline-grouped list of orgs (filter + search). Right =
 * detail panel with 4 sub-sections (Overview, Contacts, Timeline,
 * Opportunities). Above both = compact KPI strip + upcoming follow-ups.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Building2, Plus, Search, RefreshCw, Loader2, X, Star, StarOff,
  Phone, Mail, Globe, MapPin, Edit3, Trash2, Calendar, Clock,
  MessageSquare, Target, AlertCircle, ChevronRight, Filter, Download,
  Paperclip, FileText, Image as ImageIcon, Upload, ExternalLink,
  CheckCircle2, AlertTriangle, Cloud,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/partnerships`;

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '');

const STATUS_BADGE = {
  prospect: 'bg-[#FAFBFC] text-[#4B5563] border-[#E5E7EB]',
  initial_contact: 'bg-sky-50 text-sky-700 border-sky-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_hold: 'bg-slate-100 text-slate-700 border-slate-200',
  past: 'bg-[#F1F5F9] text-[#4B5563] border-[#E5E7EB]',
  declined: 'bg-rose-50 text-rose-700 border-rose-200',
};

const AdminPartnershipsTab = ({ canWrite = true }) => {
  const [vocab, setVocab] = useState({ stages: [], org_types: [], interaction_kinds: [] });
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');

  const loadVocab = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vocab`, { withCredentials: true });
      setVocab(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load vocabulary.');
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      if (statusFilter) qs.set('status', statusFilter);
      if (typeFilter) qs.set('org_type', typeFilter);
      const r = await axios.get(`${API}?${qs.toString()}`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load partnerships.');
    }
    setLoading(false);
  }, [search, statusFilter, typeFilter]);

  const loadMetrics = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard`, { withCredentials: true });
      setMetrics(r.data);
    } catch (_) { /* metrics are best-effort */ }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await axios.get(`${API}/${id}`, { withCredentials: true });
      setDetail(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load partnership detail.');
    }
  }, []);

  useEffect(() => { loadVocab(); }, [loadVocab]);
  useEffect(() => { loadList(); loadMetrics(); }, [loadList, loadMetrics]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const orgTypeLabels = useMemo(
    () => Object.fromEntries((vocab.org_types || []).map((t) => [t.id, t.label])),
    [vocab.org_types],
  );
  const stageLabels = useMemo(
    () => Object.fromEntries((vocab.stages || []).map((s) => [s.id, s.label])),
    [vocab.stages],
  );

  return (
    <div className="space-y-4" data-testid="partnerships-pane">
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs p-2 rounded inline-flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {error}
        </div>
      )}

      {/* KPI Strip */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="partnerships-kpi">
          <Kpi label="Total partners" value={metrics.total} accent="ink" />
          <Kpi label="Active" value={metrics.active} accent="emerald" />
          <Kpi label="In progress" value={metrics.in_progress} accent="amber" />
          <Kpi label="Follow-ups overdue" value={metrics.overdue_followups} accent="rose" />
          <Kpi label="Stale (60d)" value={metrics.stale_active?.length || 0} accent="slate" />
        </div>
      )}

      {/* Upcoming follow-ups */}
      {metrics?.upcoming_followups?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3"
          data-testid="partnerships-followups">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-800 inline-flex items-center gap-1.5 mb-2">
            <Clock className="w-3.5 h-3.5" /> Upcoming follow-ups (next 7 days)
          </p>
          <ul className="space-y-1">
            {metrics.upcoming_followups.map((f) => (
              <li key={f.id}
                className="text-xs flex items-center gap-2 hover:bg-amber-100 rounded px-1.5 py-1 cursor-pointer"
                onClick={() => setSelectedId(f.id)}
                data-testid={`followup-${f.id}`}>
                <Calendar className="w-3 h-3 text-amber-700" />
                <span className="font-mono text-amber-900">{f.next_followup_date}</span>
                <span className="font-semibold text-[#0A0A0A]">{f.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#4B5563] ml-auto">
                  {stageLabels[f.status] || f.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters + Add */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input type="text" value={search} placeholder="Search by name, city, tags, notes..."
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid="partnerships-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
          data-testid="partnerships-status-filter">
          <option value="">All stages</option>
          {vocab.stages.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="px-2 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
          data-testid="partnerships-type-filter">
          <option value="">All types</option>
          {vocab.org_types.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <button onClick={() => { loadList(); loadMetrics(); }}
          className="text-xs px-2.5 py-2 text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1"
          data-testid="partnerships-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <a href={`${API}/_export/full.zip`}
          className="text-xs px-2.5 py-2 border border-[#E5E7EB] rounded inline-flex items-center gap-1 hover:bg-[#FAFBFC]"
          title="Download a ZIP with 4 CSVs (orgs, contacts, interactions, opportunities)"
          data-testid="partnerships-export-btn">
          <Download className="w-3 h-3" /> Export CSV
        </a>
        {canWrite && (
          <button onClick={() => setShowCreate(true)}
            className="text-xs px-3 py-2 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 hover:bg-[#8B0F1C]"
            data-testid="partnerships-add-btn">
            <Plus className="w-3 h-3" /> Add partner
          </button>
        )}
      </div>

      {/* List / Detail split */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
        {/* LIST — grouped by status */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
            </div>
          ) : rows.length === 0 ? (
            <p className="p-6 text-center text-xs text-[#9CA3AF] italic">
              No partnerships yet. Click <strong>Add partner</strong> to start tracking your
              first organization.
            </p>
          ) : (
            <ul className="divide-y divide-[#F1F5F9] max-h-[640px] overflow-y-auto">
              {rows.map((p) => (
                <li key={p.id}>
                  <button onClick={() => setSelectedId(p.id)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-[#FAFBFC] transition-colors ${
                      selectedId === p.id ? 'bg-[#FFF8F8] border-l-2 border-l-[#BA1826]' : ''
                    }`}
                    data-testid={`partnership-row-${p.id}`}>
                    <div className="flex items-start gap-2">
                      <span className="w-7 h-7 rounded bg-[#FAFBFC] border border-[#E5E7EB] flex items-center justify-center text-[#BA1826] shrink-0">
                        <Building2 className="w-3.5 h-3.5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[#0A0A0A] truncate">{p.name}</p>
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                          {orgTypeLabels[p.org_type] || p.org_type}
                          {p.city ? ` · ${p.city}` : ''}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${STATUS_BADGE[p.status] || ''}`}>
                            {stageLabels[p.status] || p.status}
                          </span>
                          {p.next_followup_date && (
                            <span className="text-[9px] text-amber-700 inline-flex items-center gap-0.5">
                              <Calendar className="w-2.5 h-2.5" /> {p.next_followup_date}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-3 h-3 text-[#9CA3AF] mt-2 shrink-0" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* DETAIL */}
        {selectedId && detail ? (
          <PartnershipDetail
            detail={detail}
            vocab={vocab}
            canWrite={canWrite}
            onChanged={() => { loadDetail(selectedId); loadList(); loadMetrics(); }}
            onDeleted={() => { setSelectedId(null); setDetail(null); loadList(); loadMetrics(); }}
            onClose={() => { setSelectedId(null); setDetail(null); }}
          />
        ) : (
          <div className="bg-white border border-dashed border-[#E5E7EB] rounded-lg p-8 text-center">
            <Building2 className="w-8 h-8 text-[#E5E7EB] mx-auto mb-2" />
            <p className="text-xs text-[#9CA3AF]">
              Select a partnership on the left to view contacts, interactions, and opportunities.
            </p>
          </div>
        )}
      </div>

      {showCreate && (
        <PartnershipFormModal
          vocab={vocab}
          onClose={() => setShowCreate(false)}
          onSaved={(p) => { setShowCreate(false); setSelectedId(p.id); loadList(); loadMetrics(); }}
        />
      )}
    </div>
  );
};

const Kpi = ({ label, value, accent }) => {
  const colors = {
    ink: 'bg-white border-[#E5E7EB] text-[#0A0A0A]',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    slate: 'bg-[#F1F5F9] border-[#E5E7EB] text-[#4B5563]',
  };
  return (
    <div className={`border rounded p-2.5 ${colors[accent]}`}>
      <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      <p className="text-2xl font-bold font-mono">{value}</p>
    </div>
  );
};

// ============================ DETAIL PANEL ============================

const PartnershipDetail = ({ detail, vocab, canWrite, onChanged, onDeleted, onClose }) => {
  const { partnership: p, contacts, interactions, opportunities } = detail;
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const stageLabels = Object.fromEntries((vocab.stages || []).map((s) => [s.id, s.label]));
  const orgTypeLabels = Object.fromEntries((vocab.org_types || []).map((t) => [t.id, t.label]));
  const kindLabels = Object.fromEntries((vocab.interaction_kinds || []).map((k) => [k.id, k.label]));

  const handleDelete = async () => {
    if (!window.confirm(`Delete partnership "${p.name}"? This removes all contacts, timeline, and opportunities.`)) return;
    try {
      await axios.delete(`${API}/${p.id}`, { withCredentials: true });
      onDeleted();
    } catch (_) { /* surfaced as alert below */ }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/${p.id}`, { status: newStatus }, { withCredentials: true });
      onChanged();
    } catch (_) { /* silent */ }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden flex flex-col"
      data-testid="partnership-detail">
      <div className="p-4 border-b border-[#E5E7EB] flex items-start gap-3">
        <span className="w-10 h-10 rounded bg-[#FAFBFC] border border-[#E5E7EB] flex items-center justify-center text-[#BA1826]">
          <Building2 className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-base text-[#0A0A0A]" data-testid="partnership-name">{p.name}</h2>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">
            {orgTypeLabels[p.org_type] || p.org_type}
            {p.org_type_other ? ` — ${p.org_type_other}` : ''}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <select value={p.status} onChange={(e) => handleStatusChange(e.target.value)}
              disabled={!canWrite}
              className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-1 rounded border ${STATUS_BADGE[p.status] || ''}`}
              data-testid="partnership-status-select">
              {(vocab.stages || []).map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            {p.next_followup_date && (
              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-1 rounded inline-flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5" /> Follow-up {p.next_followup_date}
              </span>
            )}
            {(p.tags || []).map((t) => (
              <span key={t} className="text-[10px] bg-[#F1F5F9] border border-[#E5E7EB] px-1.5 py-1 rounded">
                #{t}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canWrite && (
            <button onClick={() => setEditing(true)}
              className="p-1.5 text-[#4B5563] hover:text-[#BA1826]"
              title="Edit partnership"
              data-testid="partnership-edit-btn">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          )}
          {canWrite && (
            <button onClick={handleDelete}
              className="p-1.5 text-[#4B5563] hover:text-rose-600"
              title="Delete partnership"
              data-testid="partnership-delete-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onClose}
            className="p-1.5 text-[#9CA3AF] hover:text-[#BA1826]"
            data-testid="partnership-close-btn">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b border-[#E5E7EB] bg-[#FAFBFC]">
        {[
          { id: 'overview', label: 'Overview', Icon: Building2 },
          { id: 'contacts', label: `Contacts (${contacts.length})`, Icon: Phone },
          { id: 'timeline', label: `Timeline (${interactions.length})`, Icon: MessageSquare },
          { id: 'opportunities', label: `Opportunities (${opportunities.length})`, Icon: Target },
          { id: 'files', label: `Files (${(detail.attachments || []).length})`, Icon: Paperclip },
        ].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 py-2 text-[11px] font-semibold inline-flex items-center gap-1.5 border-b-2 transition-all ${
              tab === id ? 'border-[#BA1826] text-[#BA1826] bg-white' : 'border-transparent text-[#4B5563]'
            }`}
            data-testid={`partnership-subtab-${id}`}>
            <Icon className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>

      <div className="p-4 overflow-y-auto max-h-[640px]">
        {tab === 'overview' && (
          <Overview p={p} stageLabels={stageLabels} />
        )}
        {tab === 'contacts' && (
          <ContactsSection p={p} contacts={contacts} canWrite={canWrite} onChanged={onChanged} />
        )}
        {tab === 'timeline' && (
          <TimelineSection p={p} interactions={interactions}
            kindLabels={kindLabels} kinds={vocab.interaction_kinds}
            canWrite={canWrite} onChanged={onChanged} />
        )}
        {tab === 'opportunities' && (
          <OpportunitiesSection p={p} opportunities={opportunities}
            canWrite={canWrite} onChanged={onChanged} />
        )}
        {tab === 'files' && (
          <AttachmentsSection p={p}
            attachments={detail.attachments || []}
            interactions={interactions} opportunities={opportunities}
            vocab={vocab} canWrite={canWrite} onChanged={onChanged} />
        )}
      </div>

      {editing && (
        <PartnershipFormModal initial={p} vocab={vocab}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }} />
      )}
    </div>
  );
};

const Overview = ({ p }) => (
  <div className="space-y-3 text-xs" data-testid="partnership-overview">
    {p.notes && (
      <div className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">Notes</p>
        <p className="text-[#0A0A0A] whitespace-pre-wrap">{p.notes}</p>
      </div>
    )}
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {p.website && <Field icon={Globe} label="Website" value={
        <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`}
          target="_blank" rel="noopener noreferrer"
          className="text-[#BA1826] hover:underline">{p.website}</a>
      } />}
      {p.phone && <Field icon={Phone} label="Phone" value={p.phone} />}
      {p.email && <Field icon={Mail} label="Email" value={
        <a href={`mailto:${p.email}`} className="text-[#BA1826] hover:underline">{p.email}</a>
      } />}
      {(p.address || p.city || p.state) && <Field icon={MapPin} label="Address" value={
        <>
          {p.address && <div>{p.address}</div>}
          <div>{[p.city, p.state, p.zip_code].filter(Boolean).join(', ')}</div>
        </>
      } />}
      {p.assigned_to && <Field label="Assigned to" value={p.assigned_to} />}
      <Field label="Created" value={fmtDate(p.created_at)} />
    </dl>
  </div>
);

const Field = ({ icon: Icon, label, value }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] inline-flex items-center gap-1">
      {Icon && <Icon className="w-2.5 h-2.5" />} {label}
    </dt>
    <dd className="text-[#0A0A0A] mt-0.5 text-xs">{value}</dd>
  </div>
);

// ============================ CONTACTS ============================

const ContactsSection = ({ p, contacts, canWrite, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (cid) => {
    if (!window.confirm('Remove this contact?')) return;
    await axios.delete(`${API}/${p.id}/contacts/${cid}`, { withCredentials: true });
    onChanged();
  };

  return (
    <div className="space-y-2" data-testid="partnership-contacts">
      {canWrite && (
        <button onClick={() => setAdding(true)}
          className="text-[11px] px-2.5 py-1.5 border border-[#BA1826]/40 text-[#BA1826] rounded inline-flex items-center gap-1 hover:bg-[#FFF8F8]"
          data-testid="contact-add-btn">
          <Plus className="w-3 h-3" /> Add contact
        </button>
      )}
      {contacts.length === 0 && (
        <p className="text-xs text-[#9CA3AF] italic">No contacts yet — add the first person at this organization.</p>
      )}
      <ul className="divide-y divide-[#F1F5F9]">
        {contacts.map((c) => (
          <li key={c.id} className="py-2.5 flex items-start gap-2"
            data-testid={`contact-row-${c.id}`}>
            <span className="w-6 h-6 rounded-full bg-[#FAFBFC] border border-[#E5E7EB] flex items-center justify-center text-[10px] font-bold text-[#BA1826] shrink-0">
              {c.name.split(' ').map((s) => s[0]).slice(0, 2).join('')}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold inline-flex items-center gap-1">
                {c.name}
                {c.is_primary && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}
              </p>
              {c.role && <p className="text-[10px] text-[#4B5563]">{c.role}</p>}
              <div className="flex flex-wrap gap-2 mt-1 text-[10px]">
                {c.email && <a href={`mailto:${c.email}`} className="text-[#BA1826] inline-flex items-center gap-0.5">
                  <Mail className="w-2.5 h-2.5" /> {c.email}
                </a>}
                {c.phone && <span className="inline-flex items-center gap-0.5 text-[#4B5563]">
                  <Phone className="w-2.5 h-2.5" /> {c.phone}
                </span>}
              </div>
              {c.notes && <p className="text-[10px] text-[#4B5563] mt-1">{c.notes}</p>}
            </div>
            {canWrite && (
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(c)} className="p-1 text-[#4B5563] hover:text-[#BA1826]"
                  data-testid={`contact-edit-${c.id}`}>
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-1 text-[#4B5563] hover:text-rose-600"
                  data-testid={`contact-delete-${c.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {(adding || editing) && (
        <ContactModal partnershipId={p.id} initial={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); onChanged(); }} />
      )}
    </div>
  );
};

// ============================ TIMELINE ============================

const TimelineSection = ({ p, interactions, kindLabels, kinds, canWrite, onChanged }) => {
  const [adding, setAdding] = useState(false);
  return (
    <div className="space-y-2" data-testid="partnership-timeline">
      {canWrite && (
        <button onClick={() => setAdding(true)}
          className="text-[11px] px-2.5 py-1.5 border border-[#BA1826]/40 text-[#BA1826] rounded inline-flex items-center gap-1 hover:bg-[#FFF8F8]"
          data-testid="interaction-add-btn">
          <Plus className="w-3 h-3" /> Log interaction
        </button>
      )}
      {interactions.length === 0 && (
        <p className="text-xs text-[#9CA3AF] italic">No interactions logged yet.</p>
      )}
      <ol className="space-y-2 mt-2">
        {interactions.map((i) => (
          <li key={i.id} className="flex gap-2.5" data-testid={`interaction-row-${i.id}`}>
            <span className="w-7 h-7 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center shrink-0 mt-0.5">
              <MessageSquare className="w-3 h-3 text-sky-700" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#0A0A0A] bg-[#F1F5F9] border border-[#E5E7EB] px-1.5 py-0.5 rounded">
                  {kindLabels[i.kind] || i.kind}
                </span>
                <span className="text-[10px] text-[#9CA3AF] font-mono">{i.occurred_on}</span>
                {i.follow_up_needed && (
                  <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5">
                    <Calendar className="w-2.5 h-2.5" /> Follow-up {i.follow_up_date || 'TBD'}
                  </span>
                )}
              </div>
              <p className="text-xs text-[#0A0A0A] mt-1 whitespace-pre-wrap">{i.summary}</p>
              {i.logged_by && (
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">Logged by {i.logged_by}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
      {adding && (
        <InteractionModal partnershipId={p.id} kinds={kinds}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); onChanged(); }} />
      )}
    </div>
  );
};

// ============================ OPPORTUNITIES ============================

const OPP_STATUSES = ['open', 'committed', 'completed', 'closed_won', 'closed_lost'];

const OpportunitiesSection = ({ p, opportunities, canWrite, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-2" data-testid="partnership-opportunities">
      {canWrite && (
        <button onClick={() => setAdding(true)}
          className="text-[11px] px-2.5 py-1.5 border border-[#BA1826]/40 text-[#BA1826] rounded inline-flex items-center gap-1 hover:bg-[#FFF8F8]"
          data-testid="opportunity-add-btn">
          <Plus className="w-3 h-3" /> Add opportunity
        </button>
      )}
      {opportunities.length === 0 && (
        <p className="text-xs text-[#9CA3AF] italic">
          No opportunities logged yet — track concrete asks (event sponsorship,
          MOU, referral pipeline, etc.).
        </p>
      )}
      <ul className="divide-y divide-[#F1F5F9]">
        {opportunities.map((o) => (
          <li key={o.id} className="py-2.5 flex items-start gap-2"
            data-testid={`opportunity-row-${o.id}`}>
            <Target className="w-3.5 h-3.5 text-[#BA1826] mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-[#0A0A0A]">{o.title}</p>
                <span className="text-[9px] uppercase tracking-wider font-bold bg-[#F1F5F9] border border-[#E5E7EB] px-1.5 py-0.5 rounded">
                  {o.status}
                </span>
                {o.estimated_value !== null && o.estimated_value !== undefined && (
                  <span className="text-[10px] text-emerald-700 font-mono">
                    ${Number(o.estimated_value).toLocaleString()}
                  </span>
                )}
              </div>
              {o.description && <p className="text-[10px] text-[#4B5563] mt-0.5">{o.description}</p>}
              {o.target_date && <p className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-0.5">
                <Calendar className="w-2.5 h-2.5" /> Target {o.target_date}
              </p>}
            </div>
            {canWrite && (
              <button onClick={() => setEditing(o)} className="p-1 text-[#4B5563] hover:text-[#BA1826]"
                data-testid={`opportunity-edit-${o.id}`}>
                <Edit3 className="w-3 h-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {(adding || editing) && (
        <OpportunityModal partnershipId={p.id} initial={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); onChanged(); }} />
      )}
    </div>
  );
};

// ============================ MODALS ============================

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    onClick={onClose} data-testid={testid}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}>
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <h3 className="font-heading font-bold text-sm">{title}</h3>
        <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#BA1826]">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  </div>
);

const Lbl = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">{children}</label>
);
const Inp = (props) => (
  <input {...props}
    className={`w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none ${props.className || ''}`} />
);
const Txt = (props) => (
  <textarea {...props}
    className={`w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none ${props.className || ''}`} />
);
const Sel = (props) => (
  <select {...props}
    className={`w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs bg-white focus:border-[#BA1826] focus:outline-none ${props.className || ''}`} />
);

const PartnershipFormModal = ({ initial, vocab, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: initial?.name || '',
    org_type: initial?.org_type || 'other',
    org_type_other: initial?.org_type_other || '',
    status: initial?.status || 'prospect',
    website: initial?.website || '',
    phone: initial?.phone || '',
    email: initial?.email || '',
    address: initial?.address || '',
    city: initial?.city || '',
    state: initial?.state || '',
    zip_code: initial?.zip_code || '',
    notes: initial?.notes || '',
    tags: (initial?.tags || []).join(', '),
    assigned_to: initial?.assigned_to || '',
    next_followup_date: initial?.next_followup_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        next_followup_date: form.next_followup_date || null,
      };
      const r = initial
        ? await axios.put(`${API}/${initial.id}`, payload, { withCredentials: true })
        : await axios.post(API, payload, { withCredentials: true });
      onSaved(r.data.partnership || r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <Modal title={initial ? 'Edit partnership' : 'Add partnership'} onClose={onClose}
      testid="partnership-form-modal">
      {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs p-2 rounded">{err}</div>}
      <div>
        <Lbl>Organization name *</Lbl>
        <Inp value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Safe Horizon, Macedonia CDC, PS 123" data-testid="pf-name" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Lbl>Type</Lbl>
          <Sel value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })}
            data-testid="pf-org-type">
            {vocab.org_types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </Sel>
        </div>
        <div>
          <Lbl>Stage</Lbl>
          <Sel value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            data-testid="pf-status">
            {vocab.stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </Sel>
        </div>
      </div>
      {form.org_type === 'other' && (
        <div>
          <Lbl>Custom type</Lbl>
          <Inp value={form.org_type_other} onChange={(e) => setForm({ ...form, org_type_other: e.target.value })}
            placeholder="e.g. Foundation, Coalition..." data-testid="pf-org-type-other" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><Lbl>Phone</Lbl><Inp value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pf-phone" /></div>
        <div><Lbl>Email</Lbl><Inp value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pf-email" /></div>
      </div>
      <div><Lbl>Website</Lbl><Inp value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." data-testid="pf-website" /></div>
      <div><Lbl>Address</Lbl><Inp value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="pf-address" /></div>
      <div className="grid grid-cols-3 gap-2">
        <div><Lbl>City</Lbl><Inp value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="pf-city" /></div>
        <div><Lbl>State</Lbl><Inp value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="pf-state" /></div>
        <div><Lbl>ZIP</Lbl><Inp value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="pf-zip" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Lbl>Assigned to</Lbl><Inp value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="pf-assigned" /></div>
        <div><Lbl>Next follow-up</Lbl><Inp type="date" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} data-testid="pf-followup" /></div>
      </div>
      <div><Lbl>Tags (comma-separated)</Lbl><Inp value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="referrals, shared-event, faith" data-testid="pf-tags" /></div>
      <div><Lbl>Notes</Lbl><Txt value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} data-testid="pf-notes" /></div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563] hover:text-[#0A0A0A]">Cancel</button>
        <button onClick={handleSave} disabled={saving}
          className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="pf-save">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} {initial ? 'Save' : 'Create'}
        </button>
      </div>
    </Modal>
  );
};

const ContactModal = ({ partnershipId, initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: initial?.name || '',
    role: initial?.role || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    is_primary: !!initial?.is_primary,
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const url = initial
        ? `${API}/${partnershipId}/contacts/${initial.id}`
        : `${API}/${partnershipId}/contacts`;
      const method = initial ? 'put' : 'post';
      await axios[method](url, form, { withCredentials: true });
      onSaved();
    } catch (_) { /* silent */ }
    setSaving(false);
  };
  return (
    <Modal title={initial ? 'Edit contact' : 'Add contact'} onClose={onClose}
      testid="contact-modal">
      <div><Lbl>Name *</Lbl><Inp value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cf-name" /></div>
      <div><Lbl>Role / title</Lbl><Inp value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="cf-role" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Lbl>Email</Lbl><Inp value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cf-email" /></div>
        <div><Lbl>Phone</Lbl><Inp value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cf-phone" /></div>
      </div>
      <label className="flex items-center gap-1.5 text-xs text-[#4B5563]">
        <input type="checkbox" checked={form.is_primary}
          onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
          data-testid="cf-primary" />
        Primary contact (gets the star)
      </label>
      <div><Lbl>Notes</Lbl><Txt value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563]">Cancel</button>
        <button onClick={handleSave} disabled={saving}
          className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="cf-save">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} {initial ? 'Save' : 'Add'}
        </button>
      </div>
    </Modal>
  );
};

const InteractionModal = ({ partnershipId, kinds, onClose, onSaved }) => {
  const [form, setForm] = useState({
    kind: 'meeting',
    occurred_on: new Date().toISOString().slice(0, 10),
    summary: '',
    follow_up_needed: false,
    follow_up_date: '',
  });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.summary.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/${partnershipId}/interactions`, {
        ...form,
        follow_up_date: form.follow_up_date || null,
      }, { withCredentials: true });
      onSaved();
    } catch (_) { /* silent */ }
    setSaving(false);
  };
  return (
    <Modal title="Log interaction" onClose={onClose} testid="interaction-modal">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Lbl>Type</Lbl>
          <Sel value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="if-kind">
            {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </Sel>
        </div>
        <div>
          <Lbl>Date</Lbl>
          <Inp type="date" value={form.occurred_on}
            onChange={(e) => setForm({ ...form, occurred_on: e.target.value })}
            data-testid="if-date" />
        </div>
      </div>
      <div>
        <Lbl>Summary *</Lbl>
        <Txt value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
          rows={4} placeholder="What was discussed? What was the outcome?"
          data-testid="if-summary" />
      </div>
      <label className="flex items-center gap-1.5 text-xs text-[#4B5563]">
        <input type="checkbox" checked={form.follow_up_needed}
          onChange={(e) => setForm({ ...form, follow_up_needed: e.target.checked })}
          data-testid="if-followup-needed" />
        Follow-up needed
      </label>
      {form.follow_up_needed && (
        <div>
          <Lbl>Follow-up date</Lbl>
          <Inp type="date" value={form.follow_up_date}
            onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
            data-testid="if-followup-date" />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563]">Cancel</button>
        <button onClick={handleSave} disabled={saving}
          className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="if-save">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
        </button>
      </div>
    </Modal>
  );
};

const OpportunityModal = ({ partnershipId, initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    status: initial?.status || 'open',
    target_date: initial?.target_date || '',
    estimated_value: initial?.estimated_value ?? '',
  });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value === '' ? null : Number(form.estimated_value),
        target_date: form.target_date || null,
      };
      if (initial) {
        await axios.put(`${API}/${partnershipId}/opportunities/${initial.id}`, payload,
          { withCredentials: true });
      } else {
        await axios.post(`${API}/${partnershipId}/opportunities`, payload,
          { withCredentials: true });
      }
      onSaved();
    } catch (_) { /* silent */ }
    setSaving(false);
  };
  return (
    <Modal title={initial ? 'Edit opportunity' : 'Add opportunity'} onClose={onClose}
      testid="opportunity-modal">
      <div><Lbl>Title *</Lbl><Inp value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="e.g. Sponsor 2026 Holiday Drive" data-testid="of-title" /></div>
      <div><Lbl>Description</Lbl><Txt value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Lbl>Status</Lbl>
          <Sel value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            data-testid="of-status">
            {OPP_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </Sel>
        </div>
        <div>
          <Lbl>Target date</Lbl>
          <Inp type="date" value={form.target_date}
            onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
        </div>
        <div>
          <Lbl>Est. value ($)</Lbl>
          <Inp type="number" min="0" value={form.estimated_value}
            onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
            data-testid="of-value" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563]">Cancel</button>
        <button onClick={handleSave} disabled={saving}
          className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="of-save">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} {initial ? 'Save' : 'Add'}
        </button>
      </div>
    </Modal>
  );
};


// ============================ ATTACHMENTS ============================

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const CATEGORY_COLORS = {
  mou: 'bg-rose-50 text-rose-700 border-rose-200',
  meeting_notes: 'bg-sky-50 text-sky-700 border-sky-200',
  flyer: 'bg-amber-50 text-amber-700 border-amber-200',
  event_invite: 'bg-violet-50 text-violet-700 border-violet-200',
  photo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partnership_doc: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  resource_list: 'bg-teal-50 text-teal-700 border-teal-200',
  other: 'bg-[#FAFBFC] text-[#4B5563] border-[#E5E7EB]',
};

const isImageMime = (m) => (m || '').startsWith('image/');

const AttachmentsSection = ({ p, attachments, interactions, opportunities,
                                  vocab, canWrite, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const categories = vocab.attachment_categories || [];
  const catLabels = Object.fromEntries(categories.map((c) => [c.id, c.label]));

  const filtered = filter === 'all'
    ? attachments
    : attachments.filter((a) => a.category === filter);

  const handleDelete = async (aid) => {
    if (!window.confirm('Delete this file? It will be removed from NHF storage and Google Drive.')) return;
    try {
      await axios.delete(`${API}/attachments/${aid}`, { withCredentials: true });
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const handleSyncDrive = async (aid) => {
    setError('');
    try {
      await axios.post(`${API}/attachments/${aid}/sync-drive`, {},
                          { withCredentials: true });
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'Drive sync failed.');
    }
  };

  return (
    <div className="space-y-3" data-testid="partnership-files">
      {canWrite && (
        <AttachmentUploader partnershipId={p.id}
          interactions={interactions} opportunities={opportunities}
          categories={categories}
          onError={setError}
          uploading={uploading} setUploading={setUploading}
          onUploaded={onChanged} />
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700
                       text-[11px] p-2 rounded inline-flex items-start gap-1.5"
          data-testid="files-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="files-filter">
          <button onClick={() => setFilter('all')}
            className={`text-[10px] px-2 py-1 rounded border ${
              filter === 'all'
                ? 'bg-[#BA1826] text-white border-[#BA1826]'
                : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#FAFBFC]'
            }`}
            data-testid="files-filter-all">
            All ({attachments.length})
          </button>
          {categories.map((c) => {
            const count = attachments.filter((a) => a.category === c.id).length;
            if (count === 0) return null;
            return (
              <button key={c.id} onClick={() => setFilter(c.id)}
                className={`text-[10px] px-2 py-1 rounded border ${
                  filter === c.id
                    ? 'bg-[#BA1826] text-white border-[#BA1826]'
                    : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#FAFBFC]'
                }`}
                data-testid={`files-filter-${c.id}`}>
                {c.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-6 text-xs text-[#9CA3AF] italic
                       border border-dashed border-[#E5E7EB] rounded">
          {attachments.length === 0
            ? 'No files yet. Drop a MOU, meeting notes, flyer, or photo above to get started.'
            : 'No files match this category.'}
        </div>
      ) : (
        <ul className="divide-y divide-[#F1F5F9] border border-[#E5E7EB] rounded">
          {filtered.map((a) => (
            <li key={a.id} className="py-2.5 px-3 flex items-start gap-2.5"
              data-testid={`file-row-${a.id}`}>
              <span className="w-7 h-7 rounded bg-[#FAFBFC] border border-[#E5E7EB]
                              flex items-center justify-center text-[#4B5563] shrink-0">
                {isImageMime(a.mime)
                  ? <ImageIcon className="w-3.5 h-3.5" />
                  : <FileText className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-xs font-semibold text-[#0A0A0A] truncate">
                    {a.file_name}
                  </p>
                  <span className={`text-[9px] uppercase tracking-wider
                                  px-1.5 py-0.5 rounded border font-bold
                                  ${CATEGORY_COLORS[a.category] || CATEGORY_COLORS.other}`}>
                    {catLabels[a.category] || a.category}
                  </span>
                  {a.drive_file_id ? (
                    <span className="text-[9px] text-emerald-700 inline-flex items-center gap-0.5"
                      title="Synced to Google Drive">
                      <CheckCircle2 className="w-2.5 h-2.5" /> Drive
                    </span>
                  ) : a.drive_sync_error ? (
                    <span className="text-[9px] text-rose-600 inline-flex items-center gap-0.5"
                      title={a.drive_sync_error}>
                      <AlertTriangle className="w-2.5 h-2.5" /> Drive failed
                    </span>
                  ) : (
                    <span className="text-[9px] text-[#9CA3AF] inline-flex items-center gap-0.5"
                      title="Not in Drive yet — push manually with the cloud button">
                      <Cloud className="w-2.5 h-2.5" /> Local only
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-[#4B5563] mt-0.5">
                  {fmtBytes(a.size_bytes)} ·{' '}
                  uploaded {fmtDate(a.uploaded_at)} by{' '}
                  {(a.uploaded_by || '').split('@')[0]}
                </p>
                {a.notes && (
                  <p className="text-[10px] text-[#4B5563] mt-0.5 italic">
                    {a.notes}
                  </p>
                )}
                {(a.interaction_id || a.opportunity_id) && (
                  <p className="text-[10px] text-[#0A6E5B] mt-0.5">
                    Linked to:{' '}
                    {a.interaction_id && <span>interaction <code>{a.interaction_id.slice(-6)}</code> </span>}
                    {a.opportunity_id && <span>opportunity <code>{a.opportunity_id.slice(-6)}</code></span>}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a href={`${API}/attachments/${a.id}/download`}
                  className="p-1 text-[#4B5563] hover:text-[#BA1826]"
                  title="Download from NHF"
                  data-testid={`file-download-${a.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </a>
                {a.drive_web_view_link && (
                  <a href={a.drive_web_view_link} target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-[#4B5563] hover:text-[#0A6E5B]"
                    title="Open in Google Drive"
                    data-testid={`file-drive-${a.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {canWrite && !a.drive_file_id && (
                  <button onClick={() => handleSyncDrive(a.id)}
                    className="p-1 text-[#4B5563] hover:text-[#0A6E5B]"
                    title="Push to Google Drive"
                    data-testid={`file-sync-${a.id}`}>
                    <Cloud className="w-3.5 h-3.5" />
                  </button>
                )}
                {canWrite && (
                  <button onClick={() => handleDelete(a.id)}
                    className="p-1 text-[#4B5563] hover:text-rose-600"
                    title="Delete"
                    data-testid={`file-delete-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AttachmentUploader = ({ partnershipId, interactions, opportunities,
                                  categories, uploading, setUploading,
                                  onUploaded, onError }) => {
  const [category, setCategory] = useState('other');
  const [notes, setNotes] = useState('');
  const [linkInteraction, setLinkInteraction] = useState('');
  const [linkOpportunity, setLinkOpportunity] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const submit = async (file) => {
    if (!file) return;
    onError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('category', category);
      if (notes) form.append('notes', notes);
      if (linkInteraction) form.append('interaction_id', linkInteraction);
      if (linkOpportunity) form.append('opportunity_id', linkOpportunity);
      await axios.post(`${API}/${partnershipId}/attachments`, form,
                          { withCredentials: true,
                            headers: { 'Content-Type': 'multipart/form-data' } });
      setNotes(''); setLinkInteraction(''); setLinkOpportunity('');
      onUploaded();
    } catch (e) {
      onError(e.response?.data?.detail || 'Upload failed.');
    }
    setUploading(false);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) submit(f);
      }}
      className={`border-2 border-dashed rounded p-3 transition-all ${
        dragOver
          ? 'border-[#BA1826] bg-[#FFF8F8]'
          : 'border-[#E5E7EB] bg-[#FAFBFC]'
      }`}
      data-testid="file-upload-zone">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded
                    bg-white"
          data-testid="file-upload-category">
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <input type="text" value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes (e.g. ‘signed copy, Jan 2026’)"
          className="text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded
                    bg-white"
          data-testid="file-upload-notes" />
        <select value={linkInteraction}
          onChange={(e) => setLinkInteraction(e.target.value)}
          className="text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded
                    bg-white"
          data-testid="file-upload-interaction">
          <option value="">(Optional) Link to interaction…</option>
          {interactions.map((i) => (
            <option key={i.id} value={i.id}>
              {i.occurred_on || '?'} — {i.kind} —{' '}
              {(i.summary || '').slice(0, 40)}
            </option>
          ))}
        </select>
        <select value={linkOpportunity}
          onChange={(e) => setLinkOpportunity(e.target.value)}
          className="text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded
                    bg-white"
          data-testid="file-upload-opportunity">
          <option value="">(Optional) Link to opportunity…</option>
          {opportunities.map((o) => (
            <option key={o.id} value={o.id}>{o.title}</option>
          ))}
        </select>
      </div>
      <label className="flex flex-col items-center justify-center py-3
                       cursor-pointer text-center">
        {uploading
          ? <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
          : <Upload className="w-6 h-6 text-[#9CA3AF]" />}
        <p className="text-xs text-[#4B5563] mt-1.5">
          {uploading
            ? 'Uploading…'
            : 'Drop a file here, or click to browse'}
        </p>
        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
          PDFs, Docs, Sheets, Slides, Photos · max 25 MB
        </p>
        <input type="file" className="hidden"
          disabled={uploading}
          onChange={(e) => { if (e.target.files?.[0]) submit(e.target.files[0]); }}
          data-testid="file-upload-input" />
      </label>
    </div>
  );
};

export default AdminPartnershipsTab;
